import { useMemo } from 'react';
import { DropdownProps, MenuProps } from 'antd';
import { ToolbarButtonProps } from './ToolbarButton';
import { ToolbarDropdownButton, renderMenuItem } from './ToolbarDropdownButton';
import {
  PictureOutlined,
  TableOutlined,
  MinusOutlined,
} from '@ant-design/icons';

import { LexicalEditor, $insertNodes } from 'lexical';
import styles from '../RichTextEditor.module.scss';
import { createPageBreakNode } from '../Nodes/PageBreakNode';
import { createDividerNode } from '../Nodes/DividerNode';
import { Icon } from '@/components/Icon';
import { useTranslation } from 'react-i18next';
import { InsertImageButton, RichTextImageProps } from './InsertImageButton';
import { InsertTableButton } from './InsertTableButton';

export interface ToolbarInsertDropdownButtonProps
  extends ToolbarButtonProps,
    Pick<DropdownProps, 'trigger' | 'overlay'> {
  selected?: InsertItemType;
}

export type InsertItemType = 'hr' | 'pagebreak' | 'image' | 'table';

export const INSERT_META_BASE: Record<
  InsertItemType,
  { icon: React.ReactNode }
> = {
  hr: { icon: <MinusOutlined /> },
  pagebreak: { icon: <Icon type="scissors" /> },
  image: { icon: <PictureOutlined /> },
  table: { icon: <TableOutlined /> },
};

function useLocalizedInsertMeta() {
  const { t } = useTranslation();

  return useMemo(() => {
    const entries = Object.entries(INSERT_META_BASE);
    return Object.fromEntries(
      entries.map(([key, val]) => [
        key,
        {
          icon: val.icon,
          label: t(`editor.insert.${key}`),
        },
      ]),
    ) as Record<InsertItemType, { label: string; icon: React.ReactNode }>;
  }, [t]);
}

export function useInsertMenu(
  editor: LexicalEditor,
  attachmentProps?: RichTextImageProps,
) {
  const meta = useLocalizedInsertMeta();

  const items = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'hr',
        label: renderMenuItem(meta.hr.icon, meta.hr.label),
        onClick: () => {
          editor.update(() => {
            $insertNodes([createDividerNode()]);
          });
        },
      },
      {
        key: 'pagebreak',
        label: renderMenuItem(meta.pagebreak.icon, meta.pagebreak.label),
        onClick: () => {
          editor.update(() => {
            $insertNodes([createPageBreakNode()]);
          });
        },
      },
      {
        key: 'image',
        label: (
          <InsertImageButton
            editor={editor}
            trigger={renderMenuItem(meta.image.icon, meta.image.label)}
            {...attachmentProps}
          />
        ),
      },
      {
        key: 'table',
        label: (
          <InsertTableButton
            editor={editor}
            trigger={renderMenuItem(meta.table.icon, meta.table.label)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editor,
      meta.hr.icon,
      meta.hr.label,
      meta.image.icon,
      meta.image.label,
      meta.pagebreak.icon,
      meta.pagebreak.label,
      meta.table.icon,
      meta.table.label,
    ],
  );

  return {
    insertMenu: items,
  };
}

export function ToolbarInsertDropdownButton(
  props: ToolbarInsertDropdownButtonProps,
) {
  const { selected, ...dropDownProps } = props;

  const meta = useLocalizedInsertMeta();
  const selectedMeta = selected ? meta[selected] : undefined;

  return (
    <ToolbarDropdownButton
      {...dropDownProps}
      selectedItem={selectedMeta}
      className={styles.insertButton}
    />
  );
}
